//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { UnisonIconType } from '@bvengineering/unison-core';
import React from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { Text } from '@components/atoms/text/Text';
import { MediumTooltip } from '@components/molecules/tooltip/Tooltip';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  iconName: UnisonIconType;
  count?: number;
  tooltipContent?: React.ReactChild;
  onClick?: (event: React.MouseEvent) => void;
}

/**
 * The OverlayIcon atom renders an icon with the default styles
 * used for icons on top of the Media atom.
 *
 * @param count If defined, will render the number next to the
 *   icon. Useful for icons like "product" to indicate the number
 *   of tagged products.
 */
const OverlayIcon = ({
  className,
  count,
  iconName,
  onClick,
  tooltipContent,
}: Props) => {
  return (
    <div className={className}>
      <MediumTooltip
        content={tooltipContent}
        isDisabled={!tooltipContent}
        target={
          <IconContainer isClickable={!!onClick} onClick={onClick}>
            <StyledIcon name={iconName} />
            {isDefined(count) && (
              <Count height={12} mixin={Mixins.Text.Caption}>
                {count}
              </Count>
            )}
          </IconContainer>
        }
      />
    </div>
  );
};

const IconContainer = styled.div<{ isClickable: boolean }>(
  ({ isClickable, theme }) => `
  cursor: ${isClickable ? 'pointer' : 'default'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacings[1]};
  box-shadow: ${theme.boxShadows[1]};
  background-color: ${theme.colors.gray0};
  border-radius: ${theme.borderRadii[2]};
`,
);

const Count = styled(Text)`
  margin-left: 4px;
`;

const StyledIcon = styled(Icon)`
  width: 12px;
  height: 12px;
`;

export default OverlayIcon;
